import { Sale } from "@sales/data/sales/SaleData";
import { SaleOverviewPage } from "./pages/SaleOverviewPage";
import SaleManageBiddersPage from "../bidders/SaleManageBiddersPage";
import SaleLotOverviewPage from "../lots/SaleLotOverviewPage";
import SaleInvoicingOverviewPage from "../invoicing/pages/SaleInvoicingOverviewPage";
import SaleSandboxPage from "./pages/SaleSandboxPage";
import { SaleQuickActionsButton } from "./components/buttons/SalesAppQuickActions";
import AgTechMultiPageTabPage from "@agtech/web/components/Pages/Tabbed/Flavors/AgTechMultiPageTabPage";
import AgTechActionPane from "@agtech/web/components/Headers/AgTechActionPane";
import { AgTechWebAppHeaderButton } from "@agtech/web/components/App/Templates/App/Layout/AgTechWebAppHeader";
import SaleDialog from "./dialogs/SaleDialog";
import { useCurrentSale } from "@sales/data/sales/SaleDataStore";
import { useSaleDataChangeSelectedSaleAction } from "@sales/data/sales/SaleDataActions";
import AgTechHeaderUserDetail from "@agtech/web/integrations/users/widgets/AgTechHeaderUserDetail";
import SalePaymentModal from "./modals/SalePaymentModal";
import AgTechNoRecordsPage from "@agtech/web/components/Pages/Error/AgTechNoRecordsPage";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { SaleDataIsSaleLicensedQuery } from "@sales/data/sales/SaleDataQueries";
import { useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";

const SaleWorkspace = (props: { sale: Sale }) => {
    const currentSale = useCurrentSale();
    const saleChangeAction = useSaleDataChangeSelectedSaleAction();

    const currentSaleLicenseQuery = useAgTechQuery({
        query: SaleDataIsSaleLicensedQuery,
        queryContext: { saleId: currentSale.id }
    });

    return (
        <AgTechQueryLoadingSurface
            query={currentSaleLicenseQuery}
            content={{
                loadingText: 'Loading sale...',
                failureText: 'Failed to load sale'
            }}
        >
            {(isSaleLicensed) => {
                const paneContent = () => (
                    <div className="flex-col tall flex">
                        <AgTechActionPane
                            classes="ml-auto !h-[40px] flex"
                            elements={[
                                <AgTechWebAppHeaderButton
                                    icon='bi bi-plus-lg'
                                    styles={{ fontSize: 32 }}
                                    action={e => SaleDialog.Open(e, {})}
                                    tooltip='Create a new sale'
                                />,
                                <AgTechWebAppHeaderButton
                                    icon='bi bi-arrow-clockwise'
                                    styles={{ fontSize: 24 }}
                                    action={async e => {
                                        await e.actions.executeDataActionWithVisual(
                                            saleChangeAction,
                                            { submittedEntity: currentSale.id },
                                            { message: 'Refreshing sale data...', delay: 1000 }
                                        )
                                    }}
                                    tooltip='Refresh current sale data'
                                />,
                                <AgTechHeaderUserDetail />,
                                <AgTechWebAppHeaderButton
                                    icon={'bi bi-grid-3x3-gap-fill'}
                                />
                            ]}
                        />
                        {isSaleLicensed && (
                            <div className="mt-auto ml-auto mb-[3px]">
                                <SaleQuickActionsButton />
                            </div>
                        )}
                    </div>
                );
            
                const tabs = isSaleLicensed ? [
                    {
                        text: 'Overview',
                        content: () => true ? <SaleOverviewPage /> : <SaleSandboxPage />
                    },
                    {
                        text: 'Manage Bidders',
                        content: () => <SaleManageBiddersPage />
                    },
                    {
                        text: 'Sell Lots',
                        content: () => <SaleLotOverviewPage />
                    },
                    {
                        text: 'Perform Invoicing',
                        content: () => <SaleInvoicingOverviewPage />
                    }
                ] : [
                    {
                        text: 'Overview',
                        content: () => (
                            <AgTechNoRecordsPage
                                message="This sale is not licensed for use."
                                messageClasses="text-xl"
                                promptClasses="text-more"
                                action={{
                                    prompt: 'To license this sale, click the button below',
                                    button: () => (
                                        <SalePaymentModal.Button
                                            classes="button-highlight py-2 px-4" 
                                            sale={currentSale}
                                            text="License Sale"
                                        />
                                    )
                                }}
                            />
                        )
                    }
                ];

                return (
                    <div className="stretched flex-col flex">
                        <AgTechMultiPageTabPage
                            title={props.sale.saleName}
                            height={120}
                            paneContent={paneContent}
                            tabs={tabs}
                            classes={{
                                header: '!border-0 !border-b rounded-none',
                                tabContainer: 'mb-[10px]',
                                tabs: {
                                    container: 'mb-2',
                                    tab: 'mr-4 px-4 py-[6px] border-none rounded font-semibold centered w-min-100 flex',
                                    inactive: 'text-secondary'
                                }
                            }}
                        />
                    </div>
                );
            }}
        </AgTechQueryLoadingSurface>
    )
}

export default SaleWorkspace